import { FC } from 'react';
import { ImageDefault, ImageProps, ImageSizes } from './ImageDefault';
import { PlaceholderDataProvider } from './PlaceholderDataProvider';

/**
 * The `Image` component facilitates seamless handling of images, ensuring they maintain the predefined aspect ratios across the UI.
 *
 * ```tsx
 * <Image
 *   src="/path-to-image.jpg"
 *   className="aspect-16/9"
 * />
 * ```
 *
 * > ⚠️ **Important Note:**
 * > Make sure to [configure the `remotePatterns`](https://nextjs.org/docs/app/api-reference/components/image#remotepatterns) for the `Image` component in your `next.config.js` file. Otherwise, you'll likely encounter errors when using images from external sources.
 *
 * ## Aspect Ratios
 *
 * There is a fixed set of supported aspect ratios you can choose from. Supply the chosen aspect ratio through the `className` property.
 *
 *
 * | Orientation | Aspect Ratio | Tailwind Class Name   |
 * |-------------|--------------|--------------|
 * | Square      | 1:1          | aspect-1/1   |
 * | Landscape   | 5:4          | aspect-5/4   |
 * | Portrait    | 4:5          | aspect-4/5   |
 * | Landscape   | 16:9         | aspect-16/9  |
 * | Portrait    | 9:16         | aspect-9/16  |
 * | Landscape   | 4:3          | aspect-4/3   |
 * | Portrait    | 3:4          | aspect-3/4   |
 * | Landscape   | 3:2          | aspect-3/2   |
 * | Portrait    | 2:3          | aspect-2/3   |
 * | Landscape   | 21:9         | aspect-21/9  |
 * | Portrait    | 9:21         | aspect-9/21  |
 * | Landscape   | 32:9         | aspect-32/9  |
 * | Portrait    | 9:32         | aspect-9/32  |
 *
 * ```tsx
 * <Image
 *   src="/path-to-image.jpg"
 *   className="aspect-16/9"
 * />
 * ```
 *
 * ## Sizes
 *
 * Provide the expected image widths in `vw` units using the responsive `sizes` property.
 * This increases the performance drastically. You don't need to provide a pixel perfect size,
 * just approach the value as best you can.
 *
 * For your convenience all numbers you provide will be transformed to `vw` units.
 * If you need to provide pixel based values, just specify them `200px`.
 *
 * ```
 * // Both props resolved to the same values.
 * <Image ... sizes={[100, {md: 50}]} />
 * <Image ... sizes={['100vw', {md: '50vw'}]} />
 * ```
 *
 * ### Examples:
 *
 * An image, which occupies is always as wide as 33% of the view port:
 *
 *  ```tsx
 * <Image src={src} sizes={100} />
 * <Image src={src} sizes="100" />
 * <Image src={src} sizes="100vw" />
 *  ```
 *
 * E.g. an image, which is displayed ...
 *  - at 100% width on the default screen
 *  - at 50% width on the `md` screen
 *  - and at 33% width on the `lg` screen
 *
 *  ```tsx
 *  <Image
 *    src={src}
 *    sizes={['100vw', {
 *      md: '50vw',
 *      lg: '33vw',
 *    }]}
 *  />
 *
 *  <Image
 *    src={src}
 *    sizes={[100, {
 *      md: 50,
 *      lg: 33,
 *    }]}
 *  />
 *  ```
 *
 * ## Compatibility with `next/image` Props
 *
 * The component is designed to be compatible with all available properties of [Next.js' `Image` component](https://nextjs.org/docs/app/api-reference/components/image). This ensures adherence and utility of Next.js' image optimization features.
 *
 * ```tsx
 * <Image
 *   src="/path-to-image.jpg"
 *   className="aspect-16/9"
 *   objectFit="contain"
 * />
 * ```
 */

const Image: FC<ImageProps> = props => {
  if (process.env.NEXT_PUBLIC_FEATURE_IMAGE_WITH_PLACEHOLDER !== 'true') {
    return <ImageDefault {...props} />;
  }

  return (
    <PlaceholderDataProvider src={props.src}>
      {placeholderProps => <ImageDefault {...placeholderProps} {...props} />}
    </PlaceholderDataProvider>
  );
};

export { Image, type ImageProps, type ImageSizes };
